import { useContext, useState } from 'react'
import { AuthContext } from '../../contexts/AuthContext'
import { PasswordHelper, Wrapper } from 'components'
import { formatDate } from 'utils/dates'
import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  Popover,
  PopoverAction,
  PopoverContent,
  toast,
} from 'keep-react'
import { format } from 'date-fns'
import { pt as ptLocale } from 'date-fns/locale'
import { Calendar, Eye, EyeSlash } from 'phosphor-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { authService } from 'api/services/auth'
import { DayPicker, type DayPickerLocale } from 'react-day-picker'
import { getCookie, setCookie } from 'cookies-next'

const dayPickerLocale: Partial<DayPickerLocale> =
  ptLocale as unknown as Partial<DayPickerLocale>

const dayPickerClassNames = {
  root: 'rdp flex w-full max-w-min rounded-xl border border-white bg-white p-5 dark:border-metal-840 dark:bg-metal-860',
  months: 'flex flex-col space-y-4 sm:space-y-0 relative dark:text-white',
  month: 'space-y-4 dark:text-white',
  month_caption: 'flex justify-center pt-[7px] relative items-center w-full ',
  caption:
    'flex justify-center pt-1 relative items-center gap-2 w-full dark:text-white',
  caption_label:
    'text-body-4 text-metal-900 dark:text-white border border-metal-300 dark:border-metal-820 w-full p-2 block rounded-lg flex justify-between [&_svg]:-rotate-90 [&_svg]:mt-[2px] dark:[&_svg]:fill-white',
  nav: 'hidden absolute z-10 space-x-1 flex justify-between flew-nowrap items-center w-full',
  dropdown_root: 'rdp-dropdown_root w-full flex-nowrap dark:text-white',
  dropdowns: 'rdp-dropdowns !gap-2 w-full ',
  button_previous:
    'h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100 inline-flex items-center justify-center whitespace-nowrap rounded-lg text-body-4 font-medium ring-offset-1 transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-white focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 hover:bg-metal-25 hover:text-metal-900 border border-metal-200 absolute top-1 left-1',
  button_next:
    'rotate-180 h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100 inline-flex items-center justify-center whitespace-nowrap rounded-lg text-body-4 font-medium ring-offset-1 transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-white focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 hover:bg-metal-25 hover:text-metal-900 border border-metal-200 absolute top-1 right-1',
  weekdays: 'flex border-y border-white dark:border-metal-820',
  weekday:
    'text-metal-400 dark:text-metal-300 rounded-xl w-10 font-normal text-body-4',
  table: 'w-full border-collapse space-y-1',
  head_row: 'flex border-y border-white dark:border-metal-820',
  head_cell:
    'text-metal-400 dark:text-metal-300 rounded-xl w-10 font-normal text-body-4',
  week: 'flex w-full mt-2',
  day: 'h-10 w-10 p-0 font-normal aria-selected:opacity-100 aria-selected:!text-white dark:text-white inline-flex items-center justify-center whitespace-nowrap text-body-4 font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring focus-visible:ring-white focus-visible:ring-offset-1 disabled:pointer-events-none day-outside disabled:opacity-50 hover:bg-metal-200 text-metal-900 rounded-full dark:hover:bg-metal-820 dark:hover:text-metal-900 dark:hover:text-white dark:opacity-100',
  selected:
    'bg-primary-500 dark:bg-primary-300 text-white dark:text-primary-900 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400',
  outside:
    '!text-metal-100 opacity-100 dark:[&_button]:text-metal-700 relative p-0 text-center text-body-4 focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-white0 [&:has([aria-selected].day-outside)]:bg-white dark:[&:has([aria-selected].day-outside)]:bg-metal-700 [&:has([aria-selected].day-outside)]:text-metal-400 [&:has([aria-selected].day-range-end)]:rounded-r-full [&:has([aria-selected])]:rounded-full first:[&:has([aria-selected])]:rounded-l-0 last:[&:has([aria-selected])]:rounded-r-0',
  disabled: '!text-metal-100 opacity-100 dark:[&_button]:text-metal-700',
  range_middle:
    'aria-selected:bg-white aria-selected:text-white0 dark:aria-selected:!bg-metal-700 dark:aria-selected:!text-white',
  hidden: 'invisible',
  chevron: 'rdp-chevron opacity-50 dark:text-white dark:opacity-100',
  today: 'dark:bg-metal-800 dark:hover:bg-metal-840 dark:hover:text-white',
}

const validationSchema = Yup.object().shape({
  id_card: Yup.string().nullable(),
  id_card_expiricy: Yup.string()
    .nullable()
    .matches(/^\d{4}[./-]\d{2}[./-]\d{2}$/, {
      message: 'A data deve ser inserida no formato AAAA-MM-DD',
      excludeEmptyString: true,
    }),
  birthdate: Yup.string()
    .nullable()
    .matches(/^\d{4}[./-]\d{2}[./-]\d{2}$/, {
      message: 'A data deve ser inserida no formato AAAA-MM-DD',
      excludeEmptyString: true,
    }),
  phone: Yup.string().nullable(),
  name: Yup.string().nullable().required('O nome é obrigatório'),
  hasMFA: Yup.boolean().nullable(),
})

const passwordValidationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Este campo é obrigatório')
    .min(10, 'A password deve ter pelo menos 10 caracteres')
    .matches(/[A-Z]/, 'A password deve conter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A password deve conter pelo menos uma letra minúscula')
    .matches(/[0-9]/, 'A password deve conter pelo menos um número')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'A password deve conter pelo menos um símbolo especial',
    ),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref('newPassword')],
    'Passwords têm de ser iguais',
  ),
})

export const Profile = () => {
  const { currentUser, setCurrentUser, currentBroker } = useContext(AuthContext)
  const [passwordFieldType, setPasswordFieldType] = useState<
    'password' | 'text'
  >('password')
  const [confirmPasswordFieldType, setConfirmPasswordFieldType] = useState<
    'password' | 'text'
  >('password')

  if (!currentUser) {
    return null
  }

  const initialValues = {
    birthdate: currentUser.Birthdate
      ? formatDate(new Date(currentUser.Birthdate).toISOString()).slashed
      : '',
    id_card_expiricy: currentUser.IdCardExpiracy
      ? formatDate(new Date(currentUser.IdCardExpiracy).toISOString()).slashed
      : null,
    id_card: currentUser.IdCard!,
    phone: currentUser.Phone || '',
    name: currentUser.Name!,
    hasMFA: currentUser.HasMFA === 'True' ? true : false,
  }

  const profileFormik = useFormik({
    initialValues: {
      birthdate: initialValues.birthdate.trim(),
      id_card_expiricy: initialValues.id_card_expiricy?.trim() || '',
      id_card: initialValues.id_card.trim(),
      phone: initialValues.phone.trim(),
      name: initialValues.name.trim(),
      hasMFA: initialValues.hasMFA,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      try {
        await authService.changeUserInformation({
          contactNo: currentUser.ContactNo!,
          name: values.name,
          birthdate: values.birthdate ? new Date(values.birthdate) : null,
          id_card: values.id_card!,
          id_card_expiricy: values.id_card_expiricy
            ? new Date(values.id_card_expiricy)
            : null,
          phone: values.phone,
          hasMFA: !!values.hasMFA,
        })
        toast.success('Informações atualizadas com sucesso!')

        const oldUserRaw = getCookie('user')

        if (oldUserRaw) {
          const newUser = JSON.parse(oldUserRaw as string)
          newUser.Name = values.name
          newUser.Birthdate = values.birthdate
            ? formatDate(new Date(values.birthdate).toISOString()).dashed
            : null
          newUser.IdCard = values.id_card!
          newUser.IdCardExpiracy = values.id_card_expiricy
            ? formatDate(new Date(values.id_card_expiricy).toISOString()).dashed
            : null
          newUser.Phone = values.phone
          setCurrentUser({ ...newUser })
          setCookie('user', JSON.stringify(newUser), { path: '/' })
        }
      } catch (error) {
        toast.error('Falha ao atualizar informações do usuário.')
        console.error('Failed to change user information:', error)
      }
    },
  })

  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: passwordValidationSchema,
    onSubmit: async values => {
      try {
        await authService.changeUserPassword({
          email: currentUser.Email!,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        })
        toast.success('Password atualizada com sucesso!')
      } catch (error) {
        toast.error('Falha ao actualizar a password.')
        console.error('Failed to change user password:', error)
      }
    },
  })

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1
          className={`text-xxxl font-medium ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
        >
          Perfil
        </h1>

        <div className="grid grid-col-1 lg:grid-cols-2 gap-4 w-full lg:py-8">
          <Card className="w-full max-w-full p-4 md:p-6">
            <h3 className="text-xl font-medium text-metal-900 pb-4">
              Dados pessoais
            </h3>
            <form onSubmit={profileFormik.handleSubmit}>
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    name="name"
                    value={profileFormik.values.name ?? ''}
                    onChange={profileFormik.handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={currentUser.Email} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={profileFormik.values.phone ?? ''}
                    onChange={profileFormik.handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="id_card">Cartão Cidadão</Label>
                  <Input
                    id="id_card"
                    name="id_card"
                    value={profileFormik.values.id_card ?? ''}
                    onChange={profileFormik.handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="id_card_expiricy">
                    Validade Cartão Cidadão
                  </Label>
                  <div>
                    <Popover>
                      <PopoverAction asChild>
                        <Button
                          color="secondary"
                          size="lg"
                          className="w-[286px] justify-start gap-2.5 border border-metal-100 text-body-4 normal-case"
                          variant="outline"
                        >
                          <Calendar
                            size={20}
                            className="text-metal-400 dark:text-white"
                          />
                          {profileFormik.values.id_card_expiricy ? (
                            format(
                              new Date(profileFormik.values.id_card_expiricy),
                              'PPP',
                              { locale: ptLocale },
                            )
                          ) : (
                            <span>Selecione a data</span>
                          )}
                        </Button>
                      </PopoverAction>
                      <PopoverContent
                        align="start"
                        className="max-w-min border-0"
                      >
                        <DayPicker
                          mode="single"
                          selected={
                            profileFormik.values.id_card_expiricy
                              ? new Date(profileFormik.values.id_card_expiricy)
                              : undefined
                          }
                          onSelect={e => {
                            profileFormik.setFieldValue(
                              'id_card_expiricy',
                              e && formatDate(e.toISOString()).dashed,
                            )
                          }}
                          captionLayout="dropdown"
                          locale={dayPickerLocale}
                          timeZone="Europe/Lisbon"
                          showOutsideDays
                          disabled={{ before: new Date() }}
                          endMonth={
                            new Date(
                              new Date().getFullYear() + 15,
                              new Date().getMonth(),
                              1,
                            )
                          }
                          startMonth={
                            new Date(
                              new Date().getFullYear(),
                              new Date().getMonth(),
                              1,
                            )
                          }
                          classNames={dayPickerClassNames}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthdate">Data de nascimento</Label>
                  <div>
                    <Popover>
                      <PopoverAction asChild>
                        <Button
                          color="secondary"
                          size="lg"
                          className="w-[286px] justify-start gap-2.5 border border-metal-100 text-body-4 normal-case"
                          variant="outline"
                        >
                          <Calendar
                            size={20}
                            className="text-metal-400 dark:text-white"
                          />
                          {profileFormik.values.birthdate ? (
                            format(
                              new Date(profileFormik.values.birthdate),
                              'PPP',
                              { locale: ptLocale },
                            )
                          ) : (
                            <span>Selecione a data</span>
                          )}
                        </Button>
                      </PopoverAction>
                      <PopoverContent
                        align="start"
                        className="max-w-min border-0"
                      >
                        <DayPicker
                          mode="single"
                          selected={
                            profileFormik.values.birthdate
                              ? new Date(profileFormik.values.birthdate)
                              : undefined
                          }
                          onSelect={e => {
                            profileFormik.setFieldValue(
                              'birthdate',
                              e && formatDate(e.toISOString()).dashed,
                            )
                          }}
                          captionLayout="dropdown"
                          locale={dayPickerLocale}
                          timeZone="Europe/Lisbon"
                          showOutsideDays
                          classNames={dayPickerClassNames}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="id_card">Cartão Cidadão</Label>
                  <Input
                    id="id_card"
                    name="id_card"
                    value={profileFormik.values.id_card ?? ''}
                    onChange={profileFormik.handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <div className="space-x-2">
                    <Label htmlFor="hasMFA">2 Factor Authentication</Label>
                    <Checkbox
                      id="hasMFA"
                      name="hasMFA"
                      checked={!!profileFormik.values.hasMFA}
                      onCheckedChange={checked =>
                        profileFormik.setFieldValue(
                          'hasMFA',
                          checked ? true : false,
                        )
                      }
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className={`${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black' : 'bg-primary-600'}`}
                  >
                    Guardar alterações
                  </Button>
                </div>
              </div>
            </form>
          </Card>
          <Card className="w-full max-w-full p-4 md:p-6">
            <h3 className="text-xl font-medium text-metal-900 pb-4">
              Mudar password
            </h3>
            <form onSubmit={passwordFormik.handleSubmit}>
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="oldPassword">Password Antiga</Label>
                  <div className="relative">
                    <Input
                      type={passwordFieldType}
                      id="oldPassword"
                      value={passwordFormik.values.oldPassword}
                      onChange={passwordFormik.handleChange}
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (passwordFieldType === 'password') {
                          setPasswordFieldType('text')
                        } else {
                          setPasswordFieldType('password')
                        }
                      }}
                    >
                      {passwordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nova Password</Label>
                  <div className="relative">
                    <Input
                      type={confirmPasswordFieldType}
                      id="newPassword"
                      value={passwordFormik.values.newPassword}
                      onChange={passwordFormik.handleChange}
                    />
                    <Button
                      type="button"
                      shape="icon"
                      color="secondary"
                      className="absolute top-1/2 right-3 -translate-y-1/2 p-0 text-metal-900 bg-transparent hover:bg-transparent"
                      onClick={() => {
                        if (confirmPasswordFieldType === 'password') {
                          setConfirmPasswordFieldType('text')
                        } else {
                          setConfirmPasswordFieldType('password')
                        }
                      }}
                    >
                      {confirmPasswordFieldType === 'password' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeSlash size={16} />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className={`${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black' : 'bg-primary-600'}`}
                  >
                    Mudar password
                  </Button>
                </div>
              </div>
            </form>
            <Card className="bg-white/70 p-4 w-full max-w-none mt-5">
              <PasswordHelper password={passwordFormik.values.newPassword} />
            </Card>
          </Card>
        </div>
      </Wrapper>
    </div>
  )
}
